import * as React from 'react'
import { Ban, Pause, Play } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

type BlastStatus = 'draft' | 'scheduled' | 'sending' | 'paused' | 'sent' | 'canceled'

export function BlastActions({
  viewOnly,
  status,
  pausePending,
  resumePending,
  pendingCount,
  onPause,
  onCancel,
  onEdit,
  onResume,
}: {
  viewOnly: boolean
  status: BlastStatus
  pausePending: boolean
  resumePending: boolean
  pendingCount: number
  onPause: () => void
  onCancel: () => void
  onEdit: () => void
  onResume: () => void
}) {
  if (viewOnly) return null
  if (status === 'sending')
    return (
      <Button variant="outline" onClick={onPause} disabled={pausePending}>
        <Pause className="mr-1.5 size-4" aria-hidden />
        Pause sending
      </Button>
    )
  if (status === 'paused')
    return (
      <>
        <Button variant="quietDestructive" onClick={onCancel}>
          <Ban className="mr-1.5 size-4" aria-hidden />
          Cancel remainder
        </Button>
        <Button variant="outline" onClick={onEdit}>
          Edit content
        </Button>
        <Button onClick={onResume} disabled={resumePending}>
          <Play className="mr-1.5 size-4" aria-hidden />
          Resume to {pendingCount.toLocaleString()} unsent
        </Button>
      </>
    )
  return null
}

export function EditPausedContentDialog({
  open,
  subject,
  body,
  pending,
  error,
  onOpenChange,
  onSave,
}: {
  open: boolean
  subject: string
  body: string
  pending: boolean
  error: boolean
  onOpenChange: (open: boolean) => void
  onSave: (content: { subject: string; body: string }) => void
}) {
  const [nextSubject, setNextSubject] = React.useState(subject)
  const [nextBody, setNextBody] = React.useState(body)
  React.useEffect(() => {
    if (!open) return
    setNextSubject(subject)
    setNextBody(body)
  }, [body, open, subject])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit paused content</DialogTitle>
          <DialogDescription>
            Changes apply only to the unsent remainder of the resolved audience.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {error && <Alert>The paused Blast content could not be saved.</Alert>}
          <div className="space-y-1.5">
            <Label htmlFor="paused-blast-subject">Subject</Label>
            <Input
              id="paused-blast-subject"
              value={nextSubject}
              onChange={(event) => setNextSubject(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="paused-blast-body">Body</Label>
            <Textarea
              id="paused-blast-body"
              value={nextBody}
              onChange={(event) => setNextBody(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave({ subject: nextSubject, body: nextBody })}
            disabled={pending}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function CancelRemainderDialog({
  open,
  pendingCount,
  pending,
  error,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  pendingCount: number
  pending: boolean
  error: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel the unsent remainder?</DialogTitle>
          <DialogDescription>
            {pendingCount.toLocaleString()} Contacts will permanently remain unsent. Deliveries
            already sent are not affected.
          </DialogDescription>
        </DialogHeader>
        {error && <Alert>The Blast remainder could not be canceled.</Alert>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Keep paused
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={pending}>
            Cancel remainder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function BlastStatusBadge({
  status,
  sentAtLabel,
}: {
  status: BlastStatus
  sentAtLabel: string | null
}) {
  if (status === 'sending') return <Badge variant="person">Sending</Badge>
  if (status === 'paused') return <Badge variant="warning">Paused</Badge>
  if (status === 'canceled') return <Badge variant="neutral">Canceled</Badge>
  return <Badge variant="success">Sent · {sentAtLabel}</Badge>
}

export function SendingProgress({
  status,
  sentCount,
  pendingCount,
  canceledCount,
}: {
  status: BlastStatus
  sentCount: number
  pendingCount: number
  canceledCount: number
}) {
  const total = sentCount + pendingCount + canceledCount
  const percent = total === 0 ? 0 : Math.round((sentCount / total) * 100)
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-3">
        <div className="flex items-baseline justify-between text-body-sm">
          <span>
            {sentCount.toLocaleString()} of {total.toLocaleString()} sent
          </span>
          <span className="text-muted-foreground">{percent}%</span>
        </div>
        <Progress value={percent} />
        {status === 'paused' && (
          <Alert variant="warning">
            Paused with {pendingCount.toLocaleString()} recipients unsent.
          </Alert>
        )}
        {status === 'canceled' && (
          <Alert>{canceledCount.toLocaleString()} recipients were permanently canceled.</Alert>
        )}
      </CardContent>
    </Card>
  )
}
